import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth, type Me } from '../lib/auth';
import { Button, ErrorBanner, Field, Input, Loading } from '../components/ui';
import { AuthFrame } from './LoginPage';

export default function InvitePage() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const info = useQuery({
    queryKey: ['invite', token],
    queryFn: () => api.get<{ email: string; firstName: string; hasPassword: boolean }>(`/auth/invite/${token}`),
    retry: false,
  });

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirm) {
      setError(new Error('The two passwords do not match.'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ user: Me }>('/auth/invite/accept', { token, password });
      setUser(res.user);
      navigate('/', { replace: true });
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthFrame>
      {info.isLoading ? (
        <Loading />
      ) : info.error ? (
        <div className="stack">
          <h1>This link has expired</h1>
          <ErrorBanner error={info.error} />
          <Link to="/forgot">Request a new link</Link>
        </div>
      ) : (
        <form className="stack" onSubmit={submit}>
          <div className="stack tight">
            <span className="eyebrow">{info.data?.hasPassword ? 'Reset password' : 'Welcome to Circdal'}</span>
            <h1>{info.data?.hasPassword ? 'Choose a new password' : `Hi ${info.data?.firstName}, set your password`}</h1>
            <p className="muted small">You will sign in as {info.data?.email}.</p>
          </div>
          <ErrorBanner error={error} />
          <Field label="New password" htmlFor="pw" hint="Use at least 10 characters.">
            <Input id="pw" type="password" autoComplete="new-password" minLength={10} required value={password} onChange={(e) => setPassword(e.target.value)} autoFocus />
          </Field>
          <Field label="Repeat the password" htmlFor="pw2">
            <Input id="pw2" type="password" autoComplete="new-password" minLength={10} required value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          </Field>
          <Button type="submit" variant="primary" busy={busy}>
            Save password and continue
          </Button>
        </form>
      )}
    </AuthFrame>
  );
}
